import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, useT } from '@zagru/platform';
import { isMockMode, mockSignOut } from '@/app/mockAuth';
import { ShellSkeleton } from '@/app/shell/ShellSkeleton';

/**
 * `/logout`: ends the session everywhere it lives — cached data in this tab, the in-memory tokens, the other tabs
 * (broadcast), and the identity provider's SSO session (end-session redirect). Without an end-session endpoint the
 * user lands on the signed-out page.
 */
export function LogoutPage() {
  const t = useT();
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      queryClient.clear();
      if (isMockMode()) {
        mockSignOut();
      } else {
        const redirected = await logout().catch(() => false);
        // The identity provider takes over and returns to the signed-out page itself.
        if (redirected) return;
      }
      if (!cancelled) setDone(true);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [logout, queryClient]);

  if (done) return <Navigate to="/login?signedOut=1" replace />;
  return <ShellSkeleton label={t('auth.signingOut')} />;
}
